import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  SOCIAL_POSTS_SORTS,
  SocialPostItem,
  SocialPostsSort,
  SocialSyncRunItem,
  SocialTrackerItem,
} from '@digital-twins/shared-types'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import Link from '@/routing/Link'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { SocialPostDetail } from './SocialPostDetail'
import { SocialPostTile } from './SocialPostTile'
import {
  askAboutTrackerHref,
  formatSocialCount,
  formatSocialDateTime,
  SOCIAL_PLATFORM_LABELS,
} from './socialFormat'
import { runStatusTone, STATUS_TONE_BADGE_VARIANT } from './socialTrackerStatus'
import { useSocialTrackerPosts, useSocialTrackerRuns } from './useSocialTrackers'
import { useSocialListeningApi } from './SocialListeningApiContext'

interface SocialTrackerDrawerProps {
  tracker: SocialTrackerItem | null
  canManage: boolean
  onClose: () => void
  /** Called after posts were removed so the tracker counts can refresh. */
  onPostsRemoved: () => void
  /** Back office: each run also shows what the provider returned and billed. */
  showRunDiagnostics?: boolean
  /** False outside the main app (the back office has no chat to ask in). */
  canAsk?: boolean
}

type PendingRemoval = { kind: 'post'; post: SocialPostItem } | { kind: 'author'; handle: string }

export function SocialTrackerDrawer(props: SocialTrackerDrawerProps) {
  const {
    tracker,
    canManage,
    onClose,
    onPostsRemoved,
    showRunDiagnostics = false,
    canAsk = true,
  } = props

  const { t, i18n } = useTranslation('app')
  const socialListeningApi = useSocialListeningApi()

  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)
  const [selectedPostToken, setSelectedPostToken] = useState<string | null>(null)
  const [sort, setSort] = useState<SocialPostsSort>('newest')
  const [topicId, setTopicId] = useState<string | null>(null)

  const trackerToken = tracker?.token ?? null
  const { posts, isLoading, isLoadingMore, hasError, hasMore, loadMore, removePosts } =
    useSocialTrackerPosts(trackerToken, sort, tracker?.lastSyncedAt ?? null, topicId)
  const topicNames = new Map((tracker?.topics ?? []).map((topic) => [topic.id, topic.name]))

  useEffect(() => {
    setSort('newest')
    setTopicId(null)
  }, [trackerToken])
  const {
    runs,
    isLoading: isLoadingRuns,
    hasError: hasRunsError,
  } = useSocialTrackerRuns(trackerToken)

  async function handleConfirmRemoval() {
    if (!pendingRemoval || !tracker) {
      return
    }
    setIsRemoving(true)
    try {
      if (pendingRemoval.kind === 'post') {
        const postToken = pendingRemoval.post.token
        await socialListeningApi.removePost(postToken)
        removePosts((post) => post.token === postToken)
        toast.success(t('socialListening.toast.postRemoved'))
      } else {
        const handle = pendingRemoval.handle
        await socialListeningApi.removeAuthor(tracker.token, handle)
        removePosts((post) => post.authorHandle === handle)
        toast.success(t('socialListening.toast.authorRemoved', { handle }))
      }
      onPostsRemoved()
      setPendingRemoval(null)
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('socialListening.toast.actionFailed')
      toast.error(message)
    } finally {
      setIsRemoving(false)
    }
  }

  const askHref = tracker && canAsk ? askAboutTrackerHref(tracker) : null
  const lastSync = formatSocialDateTime(tracker?.lastSyncedAt ?? null, i18n.language)
  const selectedPost =
    selectedPostToken == null
      ? null
      : (posts.find((post) => post.token === selectedPostToken) ?? null)

  function renderRun(run: SocialSyncRunItem) {
    const time = formatSocialDateTime(run.finishedAt ?? run.startedAt, i18n.language)
    const isFinished = run.finishedAt != null
    return (
      <li
        key={run.token}
        className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[13px]"
        data-testid={`social-run-${run.token}`}
      >
        <Badge
          variant={STATUS_TONE_BADGE_VARIANT[runStatusTone(run.status)]}
          className="rounded-full px-2 py-0 text-[11px]"
        >
          {t(`socialListening.runStatus.${run.status}`)}
        </Badge>
        <span className="text-foreground">
          {isFinished && run.itemsNew === 0
            ? t('socialListening.detail.runNoNew')
            : t('socialListening.detail.runNew', { count: run.itemsNew })}
        </span>
        {time ? <span className="text-muted-foreground">{time}</span> : null}
        {showRunDiagnostics ? (
          <Text
            as="span"
            size="xs"
            color="muted"
            className="basis-full"
            data-testid={`social-run-diagnostics-${run.token}`}
          >
            {[
              t('socialListening.detail.runFetched', {
                value: formatSocialCount(run.itemsFetched, i18n.language),
              }),
              t('socialListening.detail.runLanguageSkipped', {
                value: formatSocialCount(run.itemsLanguageSkipped, i18n.language),
              }),
              ...(run.postLimit != null
                ? [t('socialListening.detail.runPostLimit', { value: run.postLimit })]
                : []),
              ...(run.costUsd != null
                ? [t('socialListening.detail.runCost', { value: `$${run.costUsd.toFixed(3)}` })]
                : []),
            ].join(' · ')}
          </Text>
        ) : null}
        {run.itemsOffTopic > 0 ? (
          <details className="basis-full text-[12px]" data-testid={`social-run-off-topic-${run.token}`}>
            <summary className="cursor-pointer text-muted-foreground">
              {t('socialListening.detail.runOffTopic', { count: run.itemsOffTopic })}
            </summary>
            <ul className="mt-1 space-y-1 pl-4">
              {run.offTopicSamples.map((sample) => (
                <li key={sample.url} className="text-muted-foreground">
                  <a
                    href={sample.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-2 hover:underline"
                  >
                    {sample.reason}
                  </a>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        {run.error ? (
          <Text as="span" size="xs" color="muted" className="basis-full">
            {run.error}
          </Text>
        ) : null}
      </li>
    )
  }

  const postsSection = isLoading ? (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Skeleton key={i} className="aspect-[4/5] rounded-xl" />
      ))}
    </div>
  ) : hasError && posts.length === 0 ? (
    <Text size="sm" color="muted" data-testid="social-posts-error">
      {t('socialListening.detail.postsFailed')}
    </Text>
  ) : posts.length === 0 ? (
    <Text size="sm" color="muted" data-testid="social-posts-empty">
      {t('socialListening.detail.noPosts')}
    </Text>
  ) : (
    <>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="social-posts-list">
        {posts.map((post) => (
          <li key={post.token}>
            <SocialPostTile
              post={post}
              topicNames={post.topicIds.flatMap((id) => topicNames.get(id) ?? [])}
              onOpen={setSelectedPostToken}
            />
          </li>
        ))}
      </ul>
      {hasMore ? (
        <Button
          type="button"
          variant="outline"
          shape="pill"
          className="w-full"
          disabled={isLoadingMore}
          data-testid="social-posts-load-more"
          onClick={loadMore}
        >
          {isLoadingMore
            ? t('socialListening.detail.loadingMore')
            : t('socialListening.detail.loadMore')}
        </Button>
      ) : null}
    </>
  )

  const runsSection = isLoadingRuns ? (
    <Skeleton className="h-16 rounded-xl" />
  ) : hasRunsError ? (
    <Text size="sm" color="muted">
      {t('socialListening.detail.runsFailed')}
    </Text>
  ) : runs.length === 0 ? (
    <Text size="sm" color="muted">
      {t('socialListening.detail.runsEmpty')}
    </Text>
  ) : (
    <ul className="divide-y divide-border" data-testid="social-runs-list">
      {runs.map(renderRun)}
    </ul>
  )

  return (
    <>
      <Sheet
        open={tracker != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPostToken(null)
            onClose()
          }
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-[760px]"
          data-testid="social-tracker-drawer"
        >
          {tracker ? (
            <>
              <SheetHeader className="space-y-1.5 border-b border-border p-6 text-left">
                <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.04em] text-muted-foreground">
                  <SocialPlatformIcon platform={tracker.platform} className="h-3.5 w-3.5" />
                  {SOCIAL_PLATFORM_LABELS[tracker.platform]} · {tracker.market} /{' '}
                  {tracker.language.toUpperCase()}
                </div>
                <SheetTitle>{tracker.name}</SheetTitle>
                <SheetDescription>
                  {t('socialListening.detail.keywords')}:{' '}
                  {tracker.keywords.map((keyword) => `“${keyword}”`).join(', ')}
                  {tracker.handles.length > 0 ? (
                    <>
                      <br />
                      {t('socialListening.detail.handles')}: {tracker.handles.join(', ')}
                    </>
                  ) : null}
                </SheetDescription>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2">
                  <span className="text-[13px] text-foreground">
                    {t('socialListening.detail.postCount', {
                      count: tracker.totalPosts,
                      value: formatSocialCount(tracker.totalPosts, i18n.language),
                    })}
                  </span>
                  {lastSync ? (
                    <span className="text-[13px] text-muted-foreground">
                      {t('socialListening.detail.lastSync', { time: lastSync })}
                    </span>
                  ) : null}
                  {askHref ? (
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      shape="pill"
                      className="ml-auto h-9 px-4"
                    >
                      <Link href={askHref} data-testid="social-tracker-drawer-ask">
                        {t('socialListening.card.ask')}
                      </Link>
                    </Button>
                  ) : null}
                </div>
              </SheetHeader>
              <div className="space-y-8 p-6">
                {selectedPost ? (
                  <SocialPostDetail
                    post={selectedPost}
                    canManage={canManage}
                    onBack={() => setSelectedPostToken(null)}
                    onRemovePost={(post) => setPendingRemoval({ kind: 'post', post })}
                    onRemoveAuthor={(handle) => setPendingRemoval({ kind: 'author', handle })}
                  />
                ) : (
                  <>
                    <section className="space-y-3" aria-labelledby="social-posts-heading">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Heading level={3} id="social-posts-heading">
                          {t('socialListening.detail.posts')}
                        </Heading>
                        <div
                          className="flex flex-wrap gap-1.5"
                          role="radiogroup"
                          aria-label={t('socialListening.detail.sort.label')}
                          data-testid="social-posts-sort"
                        >
                          {SOCIAL_POSTS_SORTS.map((option) => {
                            const isSelected = sort === option
                            return (
                              <button
                                key={option}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                className={cn(
                                  'h-8 rounded-full border px-3 text-[13px] transition-colors',
                                  isSelected
                                    ? 'border-primary/45 bg-primary/10 text-foreground'
                                    : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                                )}
                                data-testid={`social-posts-sort-${option}`}
                                onClick={() => setSort(option)}
                              >
                                {t(`socialListening.detail.sort.${option}`)}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                      {tracker.topics.length > 0 ? (
                        <div
                          className="flex flex-wrap gap-1.5"
                          role="radiogroup"
                          aria-label={t('socialListening.detail.topicFilter')}
                          data-testid="social-posts-topic-filter"
                        >
                          {[{ id: null, name: t('socialListening.detail.allTopics') }, ...tracker.topics].map(
                            (topic) => {
                              const isSelected = topicId === topic.id
                              return (
                                <button
                                  key={topic.id ?? 'all'}
                                  type="button"
                                  role="radio"
                                  aria-checked={isSelected}
                                  className={cn(
                                    'h-7 rounded-full border px-3 text-[12px] transition-colors',
                                    isSelected
                                      ? 'border-primary/45 bg-primary/10 text-foreground'
                                      : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                                  )}
                                  data-testid={`social-posts-topic-${topic.id ?? 'all'}`}
                                  onClick={() => setTopicId(topic.id)}
                                >
                                  {topic.name}
                                </button>
                              )
                            },
                          )}
                        </div>
                      ) : null}
                      {postsSection}
                    </section>
                    <section className="space-y-2" aria-labelledby="social-runs-heading">
                      <Heading level={3} id="social-runs-heading">
                        {t('socialListening.detail.runs')}
                      </Heading>
                      {runsSection}
                    </section>
                  </>
                )}
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={pendingRemoval != null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingRemoval(null)
          }
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingRemoval?.kind === 'author'
                ? t('socialListening.detail.removeAuthorTitle', { handle: pendingRemoval.handle })
                : t('socialListening.detail.removePostTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemoval?.kind === 'author'
                ? t('socialListening.detail.removeAuthorBody')
                : t('socialListening.detail.removePostBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">
              {t('socialListening.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive text-primary-dark-foreground hover:bg-destructive/90"
              disabled={isRemoving}
              data-testid="social-remove-confirm"
              onClick={(event) => {
                event.preventDefault()
                void handleConfirmRemoval()
              }}
            >
              {t('socialListening.detail.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
